import { useEffect, useState } from 'react';

import { UIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	ANALYTICS_CHANNEL,
	ANALYTICS_FETCH_SITE_OPTOUT_FAILED,
	ANALYTICS_FETCH_SITE_OPTOUT_SUCCEEDED,
	ANALYTICS_FETCH_SITE_USER_OPTOUT_FAILED,
	ANALYTICS_FETCH_SITE_USER_OPTOUT_SUCCEEDED,
	FIRE_TRACK_ANALYTICS_DEBOUNCE_TIME,
	SITE_TRAIT_NAME_OPTOUT_ALL_MESSAGES,
	SITE_USER_TRAIT_NAME_OPTOUT_ALL_MESSAGES,
	TRACK_EVENT_TYPE,
} from './constants';
import type {
	OptoutAsyncFunctionOptions,
	OptoutOptions,
	OptoutPayload,
	OptoutStatus,
} from './types';

const getTraceId = (response: Response): string | undefined => {
	const traceIdHeader = ['atl-traceid', 'x-trace-id'].find((header) =>
		response.headers?.has(header),
	);
	return traceIdHeader === undefined ? undefined : response.headers.get(traceIdHeader) ?? undefined;
};

class FetchError extends Error {
	skipSentry: boolean;
	statusCode: number;
	traceId: string | undefined;
	originalResponse: Response | undefined;

	constructor(statusCode: number, message?: string, traceId?: string, originalResponse?: Response) {
		super(message || `Fetch call failed with status code: ${statusCode}`);
		this.name = 'FetchError';
		this.statusCode = statusCode;
		this.originalResponse = originalResponse;
		this.skipSentry = statusCode === 401;

		if (traceId) {
			this.traceId = traceId;
		}
	}
}

const getDevHostname = (): string => {
	const envDns = process.env.RDE_DNS;
	return typeof envDns === 'string' && envDns.trim() ? envDns.trim() : 'localhost';
};
const getProxyServerPort = (): number => {
	if (process.env.__PILLAR_PROXY_SERVER_PORT) {
		return parseInt(process.env.__PILLAR_PROXY_SERVER_PORT, 10);
	}

	return 9000;
};

const LOCAL_URL = new URL(
	`${process.env.__PILLAR_ENABLE_HTTPS === 'true' ? 'https' : 'http'}://${getDevHostname()}:${getProxyServerPort()}`,
);
const getReroutableURL = (pathOrURL: string): string => {
	// For local development environment only, otherwise removed during the bundling process
	if (process.env.PILLAR_LOCAL_DEVELOPMENT === 'true') {
		try {
			const u = new URL(pathOrURL, LOCAL_URL.href);
			u.host = LOCAL_URL.host;
			u.protocol = LOCAL_URL.protocol;
			return u.href;
		} catch (e) {
			// When the URL constructor has `base` set, and `pathOrUrl` is always a string,
			// no string can trigger this catch block.
			// Just in case, log the error to help with debugging.
			// eslint-disable-next-line no-console
			console.error('Failed to parse URL: ', pathOrURL, e);
			throw e;
		}
	}

	return pathOrURL;
};

const defaultOptions: RequestInit = {
	credentials: 'same-origin',
	headers: {
		'Content-Type': 'application/json',
		Accept: ['application/json', 'text/javascript', '*/*'] as any,
	},
};

let cachedFetches: Record<string, Promise<any>> = {};
let siteOptoutStatuses: Record<string, OptoutStatus> = {};
let siteUserOptoutStatuses: Record<string, Record<string, OptoutStatus>> = {};
let firedAnalyticsEvents: Record<string, Record<string, number>> = {};
let isAnalyticsDebounceEnabled = true;

export const clearCache = () => {
	siteOptoutStatuses = {};
	siteUserOptoutStatuses = {};
	cachedFetches = {};
};

const clearFetchCacheForUrl = (url: string) => {
	delete cachedFetches[url];
};

const fetchJsonWithCache = (url: string, options: RequestInit = {}) => {
	let fetchPromise = cachedFetches[url];
	if (fetchPromise) {
		return fetchPromise;
	}
	fetchPromise = fetch(getReroutableURL(url), { ...defaultOptions, ...options });
	cachedFetches[url] = fetchPromise;
	return fetchPromise;
};

const fetchJson = (url: string, options: RequestInit = {}) =>
	fetchJsonWithCache(url, options).then((response) => {
		if (!response.ok) {
			const traceId = getTraceId(response);
			if (traceId !== undefined && traceId.length > 0) {
				throw new FetchError(
					response.status,
					`Fetch call failed with status code: ${response.status}`,
					traceId,
					response,
				);
			} else {
				throw new FetchError(response.status, undefined, undefined, response);
			}
		}

		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return response.clone().json();
	});

const getPersonalizationSiteTraitsUrl = (cloudId: string) =>
	`/gateway/api/tap-delivery/api/v3/personalization/site/${cloudId}`;

const getPersonalizationSiteUserTraitsUrl = (cloudId: string, userId: string) =>
	`/gateway/api/tap-delivery/api/v3/personalization/site/${cloudId}/user/${userId}`;

const parseOptoutTraitStatus = (
	attributes: Array<{ name: string; value: string | number }>,
	attributeName: string,
) => {
	const optoutValue = attributes.find(({ name }) => name === attributeName)?.value;
	return optoutValue ? 'is-opted-out' : 'no-opt-out';
};

const parseSiteOptoutTraitStatus = (
	attributes: Array<{ name: string; value: string | number }>,
) => {
	return parseOptoutTraitStatus(attributes, SITE_TRAIT_NAME_OPTOUT_ALL_MESSAGES);
};

const parseSiteUserOptoutTraitStatus = (
	attributes: Array<{ name: string; value: string | number }>,
) => {
	return parseOptoutTraitStatus(attributes, SITE_USER_TRAIT_NAME_OPTOUT_ALL_MESSAGES);
};

const fetchSiteTraitData = async (cloudId: string) => {
	if (
		Boolean(siteOptoutStatuses[cloudId]) &&
		(siteOptoutStatuses[cloudId] === 'is-opted-out' || siteOptoutStatuses[cloudId] === 'no-opt-out')
	) {
		return { optoutStatus: siteOptoutStatuses[cloudId], cached: true };
	}

	try {
		const url = getPersonalizationSiteTraitsUrl(cloudId);
		if (siteOptoutStatuses[cloudId] === 'error') {
			clearFetchCacheForUrl(url);
		}
		const { attributes } = await fetchJson(url, {
			method: 'GET',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				'x-atlassian-mau-ignore': 'true',
			},
		});
		siteOptoutStatuses[cloudId] = parseSiteOptoutTraitStatus(attributes);
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Error fetching site personalization:', error);
		siteOptoutStatuses[cloudId] = 'error';
		throw error;
	}

	return { optoutStatus: siteOptoutStatuses[cloudId], cached: false };
};

const fetchSiteUserTraitData = async (cloudId: string, userId: string) => {
	if (
		Boolean(siteUserOptoutStatuses[cloudId]?.[userId]) &&
		(siteUserOptoutStatuses[cloudId]?.[userId] === 'is-opted-out' ||
			siteUserOptoutStatuses[cloudId]?.[userId] === 'no-opt-out')
	) {
		return { optoutStatus: siteUserOptoutStatuses[cloudId]?.[userId], cached: true };
	}

	try {
		const url = getPersonalizationSiteUserTraitsUrl(cloudId, userId);
		if (siteUserOptoutStatuses[cloudId]?.[userId] === 'error') {
			clearFetchCacheForUrl(url);
		}
		const { attributes } = await fetchJson(url, {
			method: 'GET',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				'x-atlassian-mau-ignore': 'true',
			},
		});
		siteUserOptoutStatuses[cloudId] = {
			...(siteUserOptoutStatuses[cloudId] ?? {}),
			[userId]: parseSiteUserOptoutTraitStatus(attributes),
		};
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Error fetching site user personalization:', error);
		siteUserOptoutStatuses[cloudId] = {
			...(siteUserOptoutStatuses[cloudId] ?? {}),
			[userId]: 'error',
		};
		throw error;
	}

	return { optoutStatus: siteUserOptoutStatuses[cloudId][userId], cached: false };
};

/**
 * | Site / Site user | initial      | is-opted-out | no-opt-out   | error        |
 * |------------------|--------------|--------------|--------------|--------------|
 * | initial          | initial      | is-opted-out | initial      | initial      |
 * | is-opted-out     | is-opted-out | is-opted-out | is-opted-out | is-opted-out |
 * | no-opt-out       | initial      | is-opted-out | no-opt-out   | error        |
 * | error            | initial      | is-opted-out | error        | error        |
 * @param siteOptoutValue
 * @param siteUserOptoutValue
 */
const combineOptoutValue = (
	siteOptoutValue: OptoutStatus,
	siteUserOptoutValue: OptoutStatus,
): OptoutStatus => {
	const matrix: Record<OptoutStatus, Record<OptoutStatus, OptoutStatus>> = {
		initial: {
			initial: 'initial',
			'is-opted-out': 'is-opted-out',
			'no-opt-out': 'initial',
			error: 'initial',
		},
		'is-opted-out': {
			initial: 'is-opted-out',
			'is-opted-out': 'is-opted-out',
			'no-opt-out': 'is-opted-out',
			error: 'is-opted-out',
		},
		'no-opt-out': {
			initial: 'initial',
			'is-opted-out': 'is-opted-out',
			'no-opt-out': 'no-opt-out',
			error: 'error',
		},
		error: {
			initial: 'initial',
			'is-opted-out': 'is-opted-out',
			'no-opt-out': 'error',
			error: 'error',
		},
	};
	return matrix[siteOptoutValue][siteUserOptoutValue] || 'initial';
};

export const setAnalyticsDebounceEnabled = (enabled: boolean) => {
	isAnalyticsDebounceEnabled = enabled;
};

const isAnalyticsEventFiredWithinDebounceWindow = (eventName: string, touchpointId: string) => {
	const currentTime = Date.now();
	const lastFiredTime = firedAnalyticsEvents[eventName]?.[touchpointId] ?? 0;

	if (currentTime - lastFiredTime < FIRE_TRACK_ANALYTICS_DEBOUNCE_TIME) {
		return true;
	}

	firedAnalyticsEvents[eventName] = {
		...(firedAnalyticsEvents[eventName] ?? {}),
		[touchpointId]: currentTime,
	};
	return false;
};

const fireTrackAnalytics = (
	analyticsEvent: UIAnalyticsEvent,
	eventName: string,
	attributes: Record<string, any>,
) => {
	if (
		isAnalyticsDebounceEnabled &&
		attributes.touchpointId &&
		isAnalyticsEventFiredWithinDebounceWindow(eventName, attributes.touchpointId)
	) {
		return;
	}

	const clonedEvent = analyticsEvent.clone();
	if (clonedEvent) {
		const [actionSubject, action] = eventName.split(' ');
		clonedEvent.update({
			eventType: TRACK_EVENT_TYPE,
			actionSubject,
			action,
			attributes: {
				...(clonedEvent.payload?.attributes ?? {}),
				...(attributes ?? {}),
			},
		});
		clonedEvent.fire(ANALYTICS_CHANNEL);
	}
};

export const isOptoutAllMessagesSite = async (
	touchpointId: string,
	cloudId: string,
	analyticsEvent?: UIAnalyticsEvent,
	options?: OptoutAsyncFunctionOptions,
): Promise<OptoutPayload> => {
	let optoutStatus: OptoutStatus = 'initial';
	try {
		const fetchResult = await fetchSiteTraitData(cloudId);
		optoutStatus = fetchResult.optoutStatus;
		if (analyticsEvent) {
			fireTrackAnalytics(analyticsEvent, ANALYTICS_FETCH_SITE_OPTOUT_SUCCEEDED, {
				cloudId,
				touchpointId,
				optoutStatus,
				cached: fetchResult.cached,
			});
		}
	} catch (error) {
		if (analyticsEvent) {
			fireTrackAnalytics(analyticsEvent, ANALYTICS_FETCH_SITE_OPTOUT_FAILED, {
				error: error instanceof FetchError ? error.message : 'Unknown error',
				traceId: error instanceof FetchError ? error.traceId : undefined,
				cloudId,
				touchpointId,
				optoutStatus,
			});
		}
		options?.onError?.(error as Error);
		return { optoutStatus: 'error' };
	}
	return { optoutStatus };
};

export const isOptoutAllMessagesSiteUser = async (
	touchpointId: string,
	cloudId: string,
	userId: string,
	analyticsEvent?: UIAnalyticsEvent,
	options?: OptoutAsyncFunctionOptions,
): Promise<OptoutPayload> => {
	let optoutStatus: OptoutStatus = 'initial';
	try {
		const fetchResult = await fetchSiteUserTraitData(cloudId, userId);
		optoutStatus = fetchResult.optoutStatus;
		if (analyticsEvent) {
			fireTrackAnalytics(analyticsEvent, ANALYTICS_FETCH_SITE_USER_OPTOUT_SUCCEEDED, {
				cloudId,
				userId,
				touchpointId,
				optoutStatus,
				cached: fetchResult.cached,
			});
		}
	} catch (error) {
		if (analyticsEvent) {
			fireTrackAnalytics(analyticsEvent, ANALYTICS_FETCH_SITE_USER_OPTOUT_FAILED, {
				error: error instanceof FetchError ? error.message : 'Unknown error',
				traceId: error instanceof FetchError ? error.traceId : undefined,
				cloudId,
				userId,
				touchpointId,
				optoutStatus,
			});
		}
		options?.onError?.(error as Error);
		return { optoutStatus: 'error' };
	}
	return { optoutStatus };
};

export const isOptoutAllMessages = async (
	touchpointId: string,
	cloudId: string,
	userId?: string | null,
	analyticsEvent?: UIAnalyticsEvent,
	options?: OptoutAsyncFunctionOptions,
): Promise<OptoutPayload> => {
	if (userId) {
		const [siteOptoutPayload, siteUserOptoutPayload] = await Promise.all([
			isOptoutAllMessagesSite(touchpointId, cloudId, analyticsEvent, options),
			isOptoutAllMessagesSiteUser(touchpointId, cloudId, userId, analyticsEvent, options),
		]);
		try {
			return {
				optoutStatus: combineOptoutValue(
					siteOptoutPayload.optoutStatus,
					siteUserOptoutPayload.optoutStatus,
				),
			};
		} catch (error) {
			options?.onError?.(error as Error);
			return { optoutStatus: 'error' };
		}
	}
	return isOptoutAllMessagesSite(touchpointId, cloudId, analyticsEvent, options);
};

export const useIsOptoutAllMessagesSite = (
	touchpointId: string,
	cloudId: string,
	options?: OptoutOptions & OptoutAsyncFunctionOptions,
): OptoutPayload => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [optoutStatus, setOptoutStatus] = useState<OptoutStatus>('initial');
	const isDisabled = options?.isDisabled ?? false;
	const onError = options?.onError ?? null;

	useEffect(() => {
		if (!isDisabled) {
			isOptoutAllMessagesSite(touchpointId, cloudId, createAnalyticsEvent({}), { onError }).then(
				({ optoutStatus: status }) => {
					setOptoutStatus(status);
				},
			);
		}
	}, [cloudId, isDisabled, touchpointId, createAnalyticsEvent, onError]);

	return { optoutStatus };
};

export const useIsOptoutAllMessagesSiteUser = (
	touchpointId: string,
	cloudId: string,
	userId: string,
	options?: OptoutOptions & OptoutAsyncFunctionOptions,
): OptoutPayload => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [optoutStatus, setOptoutStatus] = useState<OptoutStatus>('initial');
	const isDisabled = options?.isDisabled ?? false;
	const onError = options?.onError ?? null;

	useEffect(() => {
		if (!isDisabled) {
			isOptoutAllMessagesSiteUser(touchpointId, cloudId, userId, createAnalyticsEvent({}), {
				onError,
			}).then(({ optoutStatus: status }) => {
				setOptoutStatus(status);
			});
		}
	}, [cloudId, userId, isDisabled, touchpointId, createAnalyticsEvent, onError]);

	return { optoutStatus };
};

export const useIsOptoutAllMessages = (
	touchpointId: string,
	cloudId: string,
	userId?: string | null,
	options?: OptoutOptions & OptoutAsyncFunctionOptions,
): OptoutPayload => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [optoutStatus, setOptoutStatus] = useState<OptoutStatus>('initial');
	const isDisabled = options?.isDisabled ?? false;
	const onError = options?.onError ?? null;

	useEffect(() => {
		if (!isDisabled) {
			isOptoutAllMessages(touchpointId, cloudId, userId, createAnalyticsEvent({}), {
				onError,
			})
				.then(({ optoutStatus: status }) => {
					setOptoutStatus(status);
				})
				.catch((error) => {
					onError?.(error);
				});
		}
	}, [cloudId, userId, isDisabled, touchpointId, createAnalyticsEvent, onError]);

	return { optoutStatus };
};
